import React, { useState, useEffect } from "react";
import Dialog from "@components/Dialog";
import Text from "@components/Text";
import SizedBox from "@components/SizedBox";
import { Row, Column } from "@components/Flex";
import Button from "@components/Button";
import Loading from "@components/Loading";
import Checkbox from "@components/Checkbox";
import { toast } from "react-toastify";
import { useLang } from "@src/hooks/useLang";
import { useFarmsVM } from "@stores/FarmStore";
import { observer } from "mobx-react-lite";
import {
  getCropwiseStatus,
  getCropwiseFields,
  importCropwiseFields,
  CropwiseField,
} from "@services/cropwiseService";

interface IProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Import fields from Cropwise Operations.
 *
 * Much simpler than the John Deere equivalent: Cropwise has no
 * organization/farm/client hierarchy to walk — the API token already scopes the
 * account — so this is a single flat field list with checkboxes.
 */
const ImportFromCropwiseDialog: React.FC<IProps> = ({ visible, onClose, onSuccess }) => {
  const lang = useLang();
  const cwLang = lang.cropwise;
  const farmVM = useFarmsVM();

  const [statusLoading, setStatusLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [fields, setFields] = useState<CropwiseField[]>([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [importLoading, setImportLoading] = useState(false);
  const [needsReconnect, setNeedsReconnect] = useState(false);

  /** Cropwise errors: a rejected token needs a different call to action than a generic failure. */
  const showCropwiseError = (e: any, fallback: string) => {
    const msg = e?.message ?? fallback;
    // The backend answers 400 + needsReconnect for a dead token (never 401 —
    // that would trip the session interceptor and sign the user out).
    if (/token/i.test(msg) || /reconnect/i.test(msg)) {
      setNeedsReconnect(true);
    }
    toast.error(msg);
  };

  useEffect(() => {
    if (!visible) return;
    setStatusLoading(true);
    setNeedsReconnect(false);
    getCropwiseStatus()
      .then((s) => setConnected(s.connected))
      .catch(() => setConnected(false))
      .finally(() => setStatusLoading(false));
  }, [visible]);

  useEffect(() => {
    if (!visible || !connected) return;
    setFieldsLoading(true);
    getCropwiseFields()
      .then((r) => {
        setFields(r.fields ?? []);
        setSelectedIds(new Set());
      })
      .catch((e: any) => showCropwiseError(e, "Failed to load Cropwise fields"))
      .finally(() => setFieldsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, connected]);

  const handleToggleField = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === fields.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(fields.map((f) => f.id)));
  };

  const handleImport = async () => {
    if (selectedIds.size === 0) return;
    setImportLoading(true);
    try {
      const result = await importCropwiseFields({
        fieldIds: Array.from(selectedIds),
        farmId: farmVM.currentFarmId,
      });
      const count = result.imported?.length ?? 0;
      if (count > 0) {
        // {n} placeholder rather than an interpolated fallback — word order
        // around the number differs per language (see team.memberCount).
        toast.success(
          cwLang.importSuccess.replace(
            "{n}",
            String(count)
          )
        );
        // Importing a field with no shape is allowed but worth flagging — it
        // lands as a field with no boundary, which looks like a bug otherwise.
        const withoutGeometry = result.imported.filter((f) => !f.hasGeometry).length;
        if (withoutGeometry > 0) {
          toast.warn(
            (
              cwLang.importNoBoundary
            ).replace("{n}", String(withoutGeometry))
          );
        }
        onSuccess();
        onClose();
      }
      if (result.errors && result.errors.length > 0) {
        result.errors.forEach((err) => toast.error(`${err.fieldId}: ${err.error}`));
      }
    } catch (e: any) {
      showCropwiseError(e, "Import failed");
    } finally {
      setImportLoading(false);
    }
  };

  const canImport = selectedIds.size > 0 && !importLoading;

  return (
    <Dialog
      title={cwLang.importTitle}
      visible={visible}
      onClose={onClose}
      style={{ maxWidth: 500 }}
    >
      {statusLoading ? (
        <Loading />
      ) : !connected || needsReconnect ? (
        <Column crossAxisSize="max">
          <Text size={14} color="#6B7280">
            {needsReconnect
              ? cwLang.reconnectToImport
              : cwLang.connectToImport}
          </Text>
          <SizedBox height={16} />
          <Row justifyContent="flex-end">
            <Button onClick={onClose} kind="outline">
              {cwLang.close}
            </Button>
          </Row>
        </Column>
      ) : (
        <Column crossAxisSize="max" style={{ gap: 16 }}>
          <div>
            <Row justifyContent="space-between" alignItems="center" style={{ marginBottom: 8 }}>
              <Text size={14} style={{ fontWeight: 600 }}>
                {cwLang.fields}
              </Text>
              <Button
                kind="outline"
                onClick={handleSelectAll}
                style={{ padding: "4px 12px", maxWidth: "fit-content" }}
              >
                {fieldsLoading
                  ? "Loading..."
                  : selectedIds.size === fields.length && fields.length > 0
                  ? cwLang.deselectAll
                  : cwLang.selectAll}
              </Button>
            </Row>
            <div
              style={{
                maxHeight: 240,
                overflowY: "auto",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: 8,
              }}
            >
              {fieldsLoading ? (
                <Loading />
              ) : fields.length === 0 ? (
                <Text size={14} color="#6B7280">
                  {cwLang.noFields}
                </Text>
              ) : (
                fields.map((f) => (
                  <div
                    key={f.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "6px 0",
                      cursor: "pointer",
                    }}
                    onClick={() => handleToggleField(f.id)}
                  >
                    <span onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedIds.has(f.id)}
                        onChange={() => handleToggleField(f.id)}
                      />
                    </span>
                    <Text size={14}>
                      {f.name}
                      {f.area != null ? ` (${f.area})` : ""}
                    </Text>
                    {!f.hasGeometry && (
                      <Text size={12} color="#B45309">
                        {cwLang.noBoundaryTag}
                      </Text>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          <Row justifyContent="flex-end" style={{ gap: 8, marginTop: 8 }}>
            <Button onClick={onClose} kind="outline">
              {cwLang.cancel}
            </Button>
            <Button onClick={handleImport} disabled={!canImport}>
              {importLoading ? <Loading /> : cwLang.importButton}
            </Button>
          </Row>
        </Column>
      )}
    </Dialog>
  );
};

export default observer(ImportFromCropwiseDialog);
